<script setup lang="ts">
import SidebarLeftHeader from './SidebarLeftHeader.vue'
import SidebarLeftContent from './SidebarLeftContent.vue'
import SidebarLeftFooter from './SidebarLeftFooter.vue'

defineOptions({
  name: 'LayoutSidebarLeft',
})

// Start collapsed by default
const isOpen = ref(false)

function toggleSidebar() {
  isOpen.value = !isOpen.value
}
</script>

<template>
  <aside
    class="sidebar-left"
    :class="{
      'is-open': isOpen,
      'is-collapsed': !isOpen,
    }"
    @click.self="toggleSidebar"
  >
    <!-- Sidebar inner with clickable background -->
    <div
      class="sidebar-inner"
      @click="toggleSidebar"
    >
      <SidebarLeftHeader :is-open="isOpen" @toggle="toggleSidebar" />

      <SidebarLeftContent :is-open="isOpen" />

      <SidebarLeftFooter :is-open="isOpen" />
    </div>

    <!-- Rail (clickable edge to toggle) -->
    <button
      class="sidebar-rail"
      :aria-label="isOpen ? 'Collapse Sidebar' : 'Expand Sidebar'"
      :title="isOpen ? 'Collapse Sidebar' : 'Expand Sidebar'"
      @click="toggleSidebar"
    >
      <span class="sr-only">{{ isOpen ? 'Collapse Sidebar' : 'Expand Sidebar' }}</span>
    </button>
  </aside>
</template>

<style lang="scss" scoped>
.sidebar-left {
  @apply
    relative
    flex
    flex-col
    h-full
    bg-white
    border-r
    border-gray-200
    text-gray-900
    shrink-0
    overflow-hidden;

  display: none;
  width: 72px;
  transition: width 200ms ease-linear;

  @media (min-width: 768px) {
    display: flex;
  }

  &.is-open {
    width: 280px;
  }

  &.is-collapsed {
    width: 72px;
  }
}

// Sidebar inner
.sidebar-inner {
  @apply
    flex
    h-full
    w-full
    flex-col
    cursor-ew-resize;

  // Ensure footer stays at bottom
  display: flex;
  flex-direction: column;

  // Allow interactive elements to have normal cursor
  a, button, input, select, textarea {
    cursor: pointer;
  }
}

// Rail (clickable edge to toggle sidebar)
.sidebar-rail {
  @apply
    absolute
    inset-y-0
    -right-4
    z-20
    hidden
    w-4
    -translate-x-1/2
    transition-all
    ease-linear
    outline-none
    sm:flex
    cursor-e-resize;

  &::after {
    content: '';
    @apply
      absolute
      inset-y-0
      left-1/2
      w-[2px]
      transition-colors;
  }

  &:hover::after {
    @apply bg-gray-300;
  }

  .sidebar-left.is-open & {
    @apply cursor-w-resize;
  }
}

// Screen reader only
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}
</style>
